/* ==========================================================================
   LeeBrant Jewelry & Watch Co. — shared behavior (all pages)
   Vanilla JS, no dependencies. Include with: <script src="js/main.js" defer></script>
   ========================================================================== */

(function () {
  "use strict";

  var DESKTOP_MIN = 992;
  var reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

  function isDesktop() {
    return window.innerWidth >= DESKTOP_MIN;
  }

  /* ------------------------------------------------------------------
     1. Submenu toggles (mega menus + About Us dropdown)
        - Chevron buttons drive aria-expanded and .is-open
        - Escape closes and returns focus to the toggle
        - Click outside closes everything (desktop)
        - Only one submenu open at a time
     ------------------------------------------------------------------ */

  var subToggles = Array.prototype.slice.call(
    document.querySelectorAll(".nav-sub-toggle")
  );

  function closeSubmenu(toggle) {
    var item = toggle.closest(".primary-nav__item");
    if (!item) return;
    item.classList.remove("is-open");
    toggle.setAttribute("aria-expanded", "false");
  }

  function openSubmenu(toggle) {
    var item = toggle.closest(".primary-nav__item");
    if (!item) return;
    // Close siblings first
    subToggles.forEach(function (other) {
      if (other !== toggle) closeSubmenu(other);
    });
    item.classList.add("is-open");
    toggle.setAttribute("aria-expanded", "true");
  }

  function closeAllSubmenus() {
    subToggles.forEach(closeSubmenu);
  }

  subToggles.forEach(function (toggle) {
    toggle.setAttribute("aria-expanded", "false");

    toggle.addEventListener("click", function (event) {
      event.preventDefault();
      var expanded = toggle.getAttribute("aria-expanded") === "true";
      if (expanded) {
        closeSubmenu(toggle);
      } else {
        openSubmenu(toggle);
      }
    });
  });

  // Desktop hover: keep aria-expanded in sync with the CSS :hover reveal
  var navItems = Array.prototype.slice.call(
    document.querySelectorAll(".primary-nav__item")
  );

  navItems.forEach(function (item) {
    var toggle = item.querySelector(".nav-sub-toggle");
    if (!toggle) return;

    item.addEventListener("mouseenter", function () {
      if (isDesktop()) openSubmenu(toggle);
    });

    item.addEventListener("mouseleave", function () {
      if (isDesktop()) closeSubmenu(toggle);
    });

    // Keyboard: entering the item by focus opens the submenu through the
    // same path the click uses, so aria-expanded stays in sync with the
    // CSS :focus-within reveal and Escape works after tabbing in.
    item.addEventListener("focusin", function () {
      if (isDesktop()) openSubmenu(toggle);
    });

    // Close a submenu when keyboard focus leaves the whole item
    item.addEventListener("focusout", function (event) {
      if (isDesktop() && !item.contains(event.relatedTarget)) {
        closeSubmenu(toggle);
      }
    });
  });

  // Click outside closes any open submenu (desktop panels)
  document.addEventListener("click", function (event) {
    if (!event.target.closest(".primary-nav__item")) {
      closeAllSubmenus();
    }
  });

  /* ------------------------------------------------------------------
     2. Mobile drawer
     ------------------------------------------------------------------ */

  var navToggle = document.querySelector(".nav-toggle");
  var navToggleLabel = navToggle
    ? navToggle.querySelector(".visually-hidden")
    : null;
  var drawerClose = document.querySelector(".drawer-close");
  var scrim = document.querySelector(".nav-scrim");
  var nav = document.getElementById("primary-nav");

  function setToggleLabel(open) {
    if (navToggleLabel) {
      navToggleLabel.textContent = open ? "Close menu" : "Open menu";
    }
  }

  // While the drawer is open the rest of the page is inert (aria-hidden as
  // a fallback for browsers without the inert property).
  var inertTargets = [];

  function setBackgroundInert(on) {
    if (on) {
      inertTargets = Array.prototype.slice.call(
        document.querySelectorAll(".utility-bar, main, footer")
      );
    }
    inertTargets.forEach(function (el) {
      if ("inert" in el) {
        el.inert = on;
      } else if (on) {
        el.setAttribute("aria-hidden", "true");
      } else {
        el.removeAttribute("aria-hidden");
      }
    });
    if (!on) inertTargets = [];
  }

  function getDrawerFocusables() {
    if (!nav) return [];
    var nodes = nav.querySelectorAll(
      'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    return Array.prototype.filter.call(nodes, function (el) {
      // Skip anything hidden (e.g. links inside a closed accordion)
      return el.getClientRects().length > 0;
    });
  }

  function openDrawer() {
    document.body.classList.add("nav-open");
    if (navToggle) navToggle.setAttribute("aria-expanded", "true");
    setToggleLabel(true);
    document.body.style.overflow = "hidden";
    if (nav) {
      nav.setAttribute("role", "dialog");
      nav.setAttribute("aria-modal", "true");
    }
    setBackgroundInert(true);
    if (drawerClose) drawerClose.focus();
  }

  function closeDrawer(restoreFocus) {
    if (!document.body.classList.contains("nav-open")) return;
    document.body.classList.remove("nav-open");
    if (nav) {
      nav.removeAttribute("role");
      nav.removeAttribute("aria-modal");
    }
    setBackgroundInert(false);
    if (navToggle) {
      navToggle.setAttribute("aria-expanded", "false");
      if (restoreFocus) navToggle.focus();
    }
    setToggleLabel(false);
    document.body.style.overflow = "";
    // Always leave the drawer in a clean state so a submenu accordion
    // opened during this visit never sits stale (is-open/aria-expanded)
    // the next time the drawer opens, regardless of how it was closed.
    closeAllSubmenus();
  }

  if (navToggle) {
    navToggle.setAttribute("aria-expanded", "false");
    navToggle.addEventListener("click", function () {
      if (document.body.classList.contains("nav-open")) {
        closeDrawer(false);
      } else {
        openDrawer();
      }
    });
  }

  if (drawerClose) {
    drawerClose.addEventListener("click", function () {
      closeDrawer(true);
    });
  }

  if (scrim) {
    scrim.addEventListener("click", function () {
      closeDrawer(false);
    });
  }

  // Following any link in the drawer closes it (matters most for same-page
  // anchors, where no navigation would otherwise dismiss the drawer).
  if (nav) {
    nav.addEventListener("click", function (event) {
      if (
        document.body.classList.contains("nav-open") &&
        event.target.closest("a")
      ) {
        closeDrawer(false);
      }
    });
  }

  // Focus trap: while the drawer is open, Tab and Shift+Tab wrap within it
  document.addEventListener("keydown", function (event) {
    if (event.key !== "Tab") return;
    if (!document.body.classList.contains("nav-open")) return;

    var focusables = getDrawerFocusables();
    if (!focusables.length) return;

    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    var active = document.activeElement;
    var inside = nav && nav.contains(active);

    if (event.shiftKey) {
      if (!inside || active === first) {
        event.preventDefault();
        last.focus();
      }
    } else if (!inside || active === last) {
      event.preventDefault();
      first.focus();
    }
  });

  // Escape: close drawer first, otherwise close open submenu
  document.addEventListener("keydown", function (event) {
    if (event.key !== "Escape") return;

    if (document.body.classList.contains("nav-open")) {
      closeDrawer(true);
      return;
    }

    var openItem = document.querySelector(".primary-nav__item.is-open");
    if (openItem) {
      var toggle = openItem.querySelector(".nav-sub-toggle");
      // Focus BEFORE closing: focusing the toggle fires focusin on the
      // item, which would otherwise reopen the submenu just closed.
      if (toggle) toggle.focus();
      closeAllSubmenus();
    }
  });

  // Crossing the breakpoint resets nav state in both directions:
  // up to desktop closes the drawer (which also resets accordions);
  // down below it clears any hover/focus-opened desktop submenu so it
  // does not reappear as a stale open accordion in the drawer.
  var resizeTimer = null;
  var wasDesktop = isDesktop();
  window.addEventListener("resize", function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      var nowDesktop = isDesktop();
      if (nowDesktop) {
        closeDrawer(false);
      } else if (wasDesktop) {
        closeAllSubmenus();
      }
      wasDesktop = nowDesktop;
    }, 120);
  });

  /* ------------------------------------------------------------------
     3. Sticky header shrink on scroll
     ------------------------------------------------------------------ */

  var header = document.querySelector(".site-header");

  if (header) {
    var stuck = false;
    var onScroll = function () {
      var shouldStick = window.scrollY > 24;
      if (shouldStick !== stuck) {
        stuck = shouldStick;
        header.classList.toggle("is-stuck", stuck);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
  }

  /* ------------------------------------------------------------------
     4. Reveal on scroll (IntersectionObserver fade-up)
        Respects prefers-reduced-motion; degrades to always-visible.
     ------------------------------------------------------------------ */

  var revealEls = Array.prototype.slice.call(
    document.querySelectorAll(".reveal")
  );

  // The hidden start state in CSS is gated on html.js-reveal, so content is
  // always visible for no-JS, reduced-motion, and no-IntersectionObserver
  // visitors without any work here.
  if (
    revealEls.length &&
    !reducedMotion.matches &&
    "IntersectionObserver" in window
  ) {
    document.documentElement.classList.add("js-reveal");

    // Anything already within (or above) the first viewport becomes visible
    // immediately in the same tick, with transitions suppressed, so
    // above-the-fold content never fades in on load.
    var belowFold = [];
    var foldLine = window.innerHeight;

    revealEls.forEach(function (el) {
      if (el.getBoundingClientRect().top < foldLine) {
        el.classList.add("reveal--instant", "is-visible");
      } else {
        belowFold.push(el);
      }
    });

    if (belowFold.length) {
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              entry.target.classList.add("is-visible");
              observer.unobserve(entry.target);
            }
          });
        },
        { rootMargin: "0px 0px -8% 0px", threshold: 0.08 }
      );

      belowFold.forEach(function (el) {
        observer.observe(el);
      });
    }
  }

  /* ------------------------------------------------------------------
     5. Current year in footer: <span data-year>2026</span>
     ------------------------------------------------------------------ */

  Array.prototype.slice
    .call(document.querySelectorAll("[data-year]"))
    .forEach(function (el) {
      el.textContent = String(new Date().getFullYear());
    });
})();

/* ==========================================================================
   PDP gallery: thumbnails swap the main photo in place, and the main photo
   opens an in-page zoom overlay (no new tab). Markup lives in
   templates/product.liquid; styles in assets/commerce.css.
   ========================================================================== */

(function () {
  "use strict";

  var mainImg = document.getElementById("pdp-main-image");
  var lightbox = document.getElementById("pdp-lightbox");
  if (!mainImg || !lightbox) return;

  var trigger = document.getElementById("pdp-zoom-trigger");
  var stageImg = document.getElementById("pdp-lightbox-image");
  var stage = lightbox.querySelector(".pdp-lightbox__stage");
  var closeBtn = lightbox.querySelector(".pdp-lightbox__close");
  var prevBtn = lightbox.querySelector(".pdp-lightbox__nav--prev");
  var nextBtn = lightbox.querySelector(".pdp-lightbox__nav--next");
  var thumbs = Array.prototype.slice.call(document.querySelectorAll(".pdp__thumb"));

  var images = [];
  try {
    images = JSON.parse(document.getElementById("pdp-lightbox-data").textContent);
  } catch (e) {
    return;
  }
  if (!images.length) return;

  var current = 0;
  var zoomed = false;

  /* Thumbnails swap the main photo */
  thumbs.forEach(function (btn) {
    btn.addEventListener("click", function () {
      current = parseInt(btn.getAttribute("data-index"), 10) || 0;
      mainImg.src = btn.getAttribute("data-src");
      mainImg.srcset = btn.getAttribute("data-srcset");
      thumbs.forEach(function (b) { b.classList.remove("is-current"); });
      btn.classList.add("is-current");
    });
  });

  /* Overlay */
  function show(idx) {
    current = (idx + images.length) % images.length;
    unzoom();
    stageImg.src = images[current];
  }

  function unzoom() {
    zoomed = false;
    stageImg.classList.remove("is-zoomed");
    stageImg.style.transformOrigin = "";
  }

  function open() {
    show(current);
    lightbox.hidden = false;
    document.documentElement.style.overflow = "hidden";
    closeBtn.focus();
  }

  function close() {
    lightbox.hidden = true;
    unzoom();
    document.documentElement.style.overflow = "";
    if (trigger) trigger.focus();
  }

  function originFromEvent(evt) {
    var rect = stageImg.getBoundingClientRect();
    var x = ((evt.clientX - rect.left) / rect.width) * 100;
    var y = ((evt.clientY - rect.top) / rect.height) * 100;
    stageImg.style.transformOrigin = x + "% " + y + "%";
  }

  if (trigger) {
    trigger.addEventListener("click", open);
  }

  closeBtn.addEventListener("click", close);

  /* Click on the dark backdrop (not the photo or a control) closes */
  stage.addEventListener("click", function (evt) {
    if (evt.target === stage) close();
  });

  /* Click the large photo to magnify at the cursor; click again to reset */
  stageImg.addEventListener("click", function (evt) {
    if (zoomed) {
      unzoom();
    } else {
      zoomed = true;
      originFromEvent(evt);
      stageImg.classList.add("is-zoomed");
    }
  });

  /* While magnified, the photo pans to follow the cursor, or on a phone to
     follow a finger dragged across it. Without the touch half, a tap zoomed
     in at the tap point and left the photo stuck off to one side. */
  stage.addEventListener("pointermove", function (evt) {
    if (!zoomed) return;
    if (evt.pointerType === "mouse" || evt.buttons) originFromEvent(evt);
  });

  if (prevBtn) prevBtn.addEventListener("click", function () { show(current - 1); });
  if (nextBtn) nextBtn.addEventListener("click", function () { show(current + 1); });

  document.addEventListener("keydown", function (evt) {
    if (lightbox.hidden) return;
    if (evt.key === "Escape") close();
    if (evt.key === "ArrowLeft" && prevBtn) show(current - 1);
    if (evt.key === "ArrowRight" && nextBtn) show(current + 1);
  });
})();

/* ==========================================================================
   Load-more pagination: "Show More Pieces" / "More Results" appends the next
   page's cards to the grid in place instead of navigating. The links keep
   real hrefs (with a #pieces anchor) so pagination still works without
   JavaScript, search engines still crawl every page, and Previous still
   behaves as a normal link.
   ========================================================================== */

(function () {
  "use strict";

  var loading = false;

  document.addEventListener("click", function (evt) {
    var link = evt.target.closest ? evt.target.closest('.pager a[rel="next"]') : null;
    if (!link || loading) return;

    var pager = link.closest(".catalog-more");
    var grid = pager ? pager.parentElement.querySelector(".product-grid") : null;
    if (!grid) return; // unexpected layout: let the normal link do its job

    evt.preventDefault();
    loading = true;
    link.setAttribute("aria-busy", "true");
    var label = link.textContent;
    link.textContent = "Loading...";

    fetch(link.href)
      .then(function (res) { return res.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, "text/html");
        var newGrid = doc.querySelector(".product-grid");
        var newPager = doc.querySelector(".catalog-more");

        if (!newGrid) throw new Error("no grid in next page");

        while (newGrid.firstChild) {
          grid.appendChild(newGrid.firstChild);
        }

        if (newPager && newPager.querySelector('a[rel="next"]')) {
          pager.replaceWith(newPager);
        } else if (newPager) {
          // Last page: keep the pager (Previous + status) but there is no
          // further next link to click.
          pager.replaceWith(newPager);
        } else {
          pager.remove();
        }

        // Keep the address bar honest so refresh/back lands near where
        // the visitor actually is.
        try { history.replaceState(null, "", link.href); } catch (e) { /* no-op */ }
      })
      .catch(function () {
        // Network or parse trouble: fall back to plain navigation.
        link.textContent = label;
        window.location.href = link.href;
      })
      .finally(function () {
        loading = false;
      });
  });
})();

/* ==========================================================================
   High Luxury page: "View the Full High Luxury Collection" expands the grid
   in place. It fetches every page of the high-luxury collection and swaps
   the 12 featured cards for the complete set, so the visitor never leaves
   the page. The href is the no-JavaScript fallback.
   ========================================================================== */

(function () {
  "use strict";

  var btn = document.getElementById("hl-expand");
  if (!btn) return;

  btn.addEventListener("click", function (evt) {
    evt.preventDefault();
    if (btn.getAttribute("aria-busy")) return;

    var grid = btn.closest(".container").querySelector(".product-grid");
    if (!grid) { window.location.href = btn.href; return; }

    btn.setAttribute("aria-busy", "true");
    btn.textContent = "Loading the Full Collection...";

    var base = btn.getAttribute("data-collection-url");
    var cards = [];

    function fetchPage(n) {
      return fetch(base + "?page=" + n)
        .then(function (res) { return res.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, "text/html");
          var g = doc.querySelector(".product-grid");
          if (!g) return false;
          while (g.firstChild) { cards.push(g.firstChild); g.removeChild(g.firstChild); }
          return !!doc.querySelector('.pager a[rel="next"]');
        });
    }

    function fetchAll(n) {
      if (n > 10) return Promise.resolve(); // runaway guard
      return fetchPage(n).then(function (hasMore) {
        if (hasMore) return fetchAll(n + 1);
      });
    }

    fetchAll(1)
      .then(function () {
        if (!cards.length) throw new Error("empty");
        grid.innerHTML = "";
        cards.forEach(function (c) { grid.appendChild(c); });
        btn.parentElement.remove();
      })
      .catch(function () {
        // Trouble loading: fall back to the collection page itself.
        window.location.href = btn.href;
      });
  });
})();

/* ==========================================================================
   Calendly booking. Every appointment link ("Book an Appointment",
   "Request a Private Consultation") opens a Calendly popup over the page
   instead of navigating away. The link href (/pages/contact) stays as the
   no-JavaScript fallback. The event type matches the page the visitor is
   on; anywhere else they get the landing page with all three options.
   ========================================================================== */

(function () {
  "use strict";

  var LANDING = "https://calendly.com/leebrant";
  var BY_PAGE = [
    { path: "/pages/engagement", url: LANDING + "/engagement-consultation" },
    { path: "/pages/rolex-and-watch-repair", url: LANDING + "/watch-service-consultation" },
    { path: "/products/", url: LANDING + "/fine-jewelry-or-pre-owned-watch-shopping" }
  ];

  function bookingUrl() {
    var here = window.location.pathname;
    for (var i = 0; i < BY_PAGE.length; i++) {
      if (here.indexOf(BY_PAGE[i].path) !== -1) return BY_PAGE[i].url;
    }
    return LANDING;
  }

  var state = "idle"; // idle -> loading -> ready
  var openWhenReady = false;

  function openPopup() {
    window.Calendly.initPopupWidget({ url: bookingUrl() + "?hide_gdpr_banner=1" });
  }

  function ensureCalendly() {
    if (state === "ready") { openPopup(); return; }
    openWhenReady = true;
    if (state === "loading") return;
    state = "loading";

    var css = document.createElement("link");
    css.rel = "stylesheet";
    css.href = "https://assets.calendly.com/assets/external/widget.css";
    document.head.appendChild(css);

    var js = document.createElement("script");
    js.src = "https://assets.calendly.com/assets/external/widget.js";
    js.async = true;
    js.onload = function () {
      state = "ready";
      if (openWhenReady) openPopup();
    };
    js.onerror = function () {
      // Calendly unreachable: fall back to the contact page.
      window.location.href = "/pages/contact";
    };
    document.head.appendChild(js);
  }

  document.addEventListener("click", function (evt) {
    var a = evt.target.closest ? evt.target.closest("a") : null;
    if (!a) return;
    var label = (a.textContent || "").trim().toLowerCase();
    if (label.indexOf("book an appointment") === -1 &&
        label.indexOf("request a private consultation") === -1) return;
    evt.preventDefault();
    ensureCalendly();
  });
})();
